import { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const Wrapper = styled.div`
  margin: 40px 0;
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;

  @media (min-width: 765px) {
    display: none;
  }
`;

const ArrowButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--bg-300);
  background-color: var(--bg-100);
  color: var(--text-100);
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
  &:not(:disabled):hover {
    background-color: var(--bg-200);
  }
`;

const Track = styled.div`
  display: flex;
  overflow: hidden;
`;

const TrackInner = styled.div`
  display: flex;
  width: 100%;
  transition: transform 0.3s ease-in-out;
  transform: translateX(${(props) => -props.$index * 100}%);

  @media (min-width: 765px) {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    transform: none;
  }
`;

const Card = styled.div`
  flex: 0 0 100%;
  box-sizing: border-box;
  background-color: #dfeddb;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 220px;

  @media (min-width: 765px) {
    flex: unset;
  }
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CardTitle = styled.h3`
  font-size: var(--font-size-large);
  color: var(--text-100);
  font-weight: 600;
  margin: 0;
`;

const CardText = styled.p`
  font-size: var(--font-size-small);
  color: var(--text-100);
  margin: 0;
  @media (min-width: 765px) {
    font-size: var(--font-size-base);
  }
`;

const Bold = styled.p`
  font-size: var(--font-size-small);
  color: var(--text-100);
  font-weight: 700;
  margin: 8px 0 0 0;
  @media (min-width: 765px) {
    font-size: var(--font-size-base);
  }
`;

const EmailLink = styled.a`
  font-weight: 700;
  color: var(--text-100);
  text-decoration: underline;
`;

const ChatButton = styled.a`
  align-self: flex-start;
  margin-top: auto;
  padding: 10px 20px;
  background-color: var(--bg-100);
  color: var(--text-100);
  border: none;
  border-radius: 4px;
  font-size: var(--font-size-small);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  &::before {
    content: "•";
  }
`;

const Icon = ({ children }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--text-100)"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const ContactCardsSection = () => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const maxIndex = 2;

  const handlePrev = () => setIndex((i) => Math.max(i - 1, 0));
  const handleNext = () => setIndex((i) => Math.min(i + 1, maxIndex));

  return (
    <Wrapper>
      <Track>
        <TrackInner $index={index}>
          <Card>
            <CardHead>
              <CardTitle>{t("CONTACT_SECTION.CHAT_TITLE")}</CardTitle>
              <Icon>
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </Icon>
            </CardHead>
            <CardText>{t("CONTACT_SECTION.CHAT_TEXT")}</CardText>
            <Bold>{t("CONTACT_SECTION.CHAT_HOURS_TITLE")}</Bold>
            <CardText>{t("CONTACT_SECTION.CHAT_HOURS")}</CardText>
            <ChatButton href="mailto:info@snusco.com">
              {t("CONTACT_SECTION.CHAT_BUTTON")}
            </ChatButton>
          </Card>

          <Card>
            <CardHead>
              <CardTitle>{t("CONTACT_SECTION.EMAIL_TITLE")}</CardTitle>
              <Icon>
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M3 7l9 6 9-6" />
              </Icon>
            </CardHead>
            <CardText>{t("CONTACT_SECTION.EMAIL_TEXT")}</CardText>
            <EmailLink href="mailto:info@snusco.com">info@snusco.com</EmailLink>
            <CardText>{t("CONTACT_SECTION.EMAIL_FOOTER")}</CardText>
          </Card>

          <Card>
            <CardHead>
              <CardTitle>{t("CONTACT_SECTION.CALL_TITLE")}</CardTitle>
              <Icon>
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
              </Icon>
            </CardHead>
            <Bold>
              {t("CONTACT_SECTION.CALL_LABEL")} +43 667 7628134
            </Bold>
            <Bold>{t("CONTACT_SECTION.CALL_HOURS_TITLE")}</Bold>
            <CardText>{t("CONTACT_SECTION.CALL_HOURS")}</CardText>
          </Card>
        </TrackInner>
      </Track>

      <NavRow>
        <ArrowButton onClick={handlePrev} disabled={index === 0} aria-label="Previous">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 6l-6 6 6 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </ArrowButton>
        <ArrowButton onClick={handleNext} disabled={index === maxIndex} aria-label="Next">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 6l6 6-6 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </ArrowButton>
      </NavRow>
    </Wrapper>
  );
};

export default ContactCardsSection;
